import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const QUESTION_TYPES = [
  { value: 'text', label: 'Text (open answer)' },
  { value: 'single', label: 'Single choice' },
  { value: 'multi', label: 'Multiple choice' },
  { value: 'rating', label: 'Rating (1-5)' }
];

const generateId = () => `q${Date.now()}${Math.floor(Math.random() * 1000)}`;

const emptyQuestion = () => ({
  id: generateId(),
  type: 'single',
  label: '',
  options: ['', ''],
  required: false
});

const SurveyEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/admin/surveys');
    }
  }, [user, navigate]);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [questions, setQuestions] = useState([emptyQuestion()]);

  useEffect(() => {
    if (!isEditing) return;

    const token = localStorage.getItem('token');
    if (token && !axios.defaults.headers.common['Authorization']) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const fetchSurvey = async () => {
      try {
        const response = await axios.get(`/api/surveys/${id}`);
        const survey = response.data.survey;
        setTitle(survey.title);
        setDescription(survey.description || '');
        setSlug(survey.slug);
        setIsActive(survey.isActive);
        setQuestions(survey.questions.length > 0 ? survey.questions : [emptyQuestion()]);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not load survey');
        navigate('/admin/surveys');
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [id, isEditing, navigate]);

  const updateQuestion = (questionId, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, [field]: value } : q))
    );
  };

  const updateOption = (questionId, index, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        const options = [...q.options];
        options[index] = value;
        return { ...q, options };
      })
    );
  };

  const addOption = (questionId) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, options: [...q.options, ''] } : q))
    );
  };

  const removeOption = (questionId, index) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        const options = q.options.filter((_, i) => i !== index);
        return { ...q, options };
      })
    );
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, emptyQuestion()]);
  };

  const removeQuestion = (questionId) => {
    if (questions.length <= 1) {
      toast.error('A survey needs at least one question');
      return;
    }
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  const moveQuestion = (index, direction) => {
    setQuestions((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');
    if (token && !axios.defaults.headers.common['Authorization']) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const payload = {
      title,
      description,
      slug,
      isActive,
      questions
    };

    try {
      if (isEditing) {
        await axios.put(`/api/surveys/${id}`, payload);
        toast.success('Survey updated');
      } else {
        const response = await axios.post('/api/surveys', payload);
        toast.success('Survey created');
        if (payload.isActive) {
          toast.success('Survey activated');
        }
        navigate(`/admin/surveys/${response.data.survey._id}/edit`);
        return;
      }
      navigate('/admin/surveys');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save survey');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!slug) return;
    const url = `${window.location.origin}/survey/${slug}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Survey link copied to clipboard'))
      .catch(() => toast.error('Could not copy link'));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const questionField = (question) => {
    if (question.type === 'text') {
      return <input disabled placeholder="Free text answer" className="w-full px-3 py-2 bg-tertiary border border-primary rounded-lg text-muted" />;
    }
    if (question.type === 'rating') {
      return <div className="flex gap-3">{[1, 2, 3, 4, 5].map((n) => <span key={n} className="w-12 h-12 inline-flex items-center justify-center bg-tertiary border border-primary rounded-lg text-base font-semibold text-muted">{n}</span>)}</div>;
    }
    return question.options.filter((o) => o.trim() !== '').map((o, i) => (
      <div key={i} className="flex items-center gap-2">
        {question.type === 'single' ? (
          <input type="radio" disabled className="h-4 w-4 text-indigo-600" />
        ) : (
          <input type="checkbox" disabled className="h-4 w-4 text-indigo-600" />
        )}
        <span className="text-sm text-secondary">{o}</span>
      </div>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{isEditing ? 'Edit Survey' : 'New Survey'}</h1>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 text-sm font-semibold text-secondary bg-secondary border border-secondary rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="px-4 py-2 text-sm font-semibold text-secondary bg-secondary border border-secondary rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {showPreview ? (
        <div className="bg-card rounded-xl shadow overflow-hidden p-6 sm:p-10">
          <div className="border-b border-primary pb-6">
            <h1 className="text-2xl font-bold text-primary">{title || 'Untitled Survey'}</h1>
            {description && <p className="mt-2 text-secondary">{description}</p>}
          </div>
          <div className="pt-8 space-y-8">
            {questions.map((question, index) => (
              <div key={question.id}>
                <label className="block text-sm font-medium text-primary mb-2">
                  <span className="text-muted mr-2">{index + 1}.</span>
                  {question.label || `Question ${index + 1}`}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="space-y-2">{questionField(question)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="bg-card rounded-xl shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Settings</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm font-medium text-secondary">Make live now</span>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 rounded-full bg-gray-300 dark:bg-gray-600 peer-checked:bg-green-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-5" />
              </label>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="form-input w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Class Preferences Survey"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Description</label>
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-input w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Help us build a timetable that works for you..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Public link slug</label>
                <div className="flex items-center">
                  <span className="text-sm text-muted mr-1">/survey/</span>
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    className="form-input w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="class-preferences"
                  />
                  {slug && (
                    <button
                      type="button"
                      onClick={copyLink}
                      className="ml-2 inline-flex items-center px-3 py-2 text-sm font-semibold text-secondary bg-secondary border border-secondary rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Copy link
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted mt-1">Leave blank to auto-generate from the title.</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Questions</h2>
              <button
                type="button"
                onClick={addQuestion}
                className="inline-flex items-center px-3 py-1.5 text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20 transition-colors"
              >
                + Add Question
              </button>
            </div>

            <div className="space-y-4">
              {questions.map((question, index) => (
                <div key={question.id} className="border border-primary rounded-lg p-4 bg-card dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-secondary">Question {index + 1}</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, -1)}
                        disabled={index === 0}
                        className="text-muted hover:text-secondary disabled:opacity-30"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, 1)}
                        disabled={index === questions.length - 1}
                        className="text-muted hover:text-secondary disabled:opacity-30"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeQuestion(question.id)}
                        className="text-red-400 hover:text-red-600 ml-2"
                        title="Remove question"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-tertiary mb-1">Type</label>
                      <select
                        value={question.type}
                        onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                        className="form-input w-full px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {QUESTION_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-tertiary mb-1">Question text *</label>
                      <input
                        type="text"
                        value={question.label}
                        onChange={(e) => updateQuestion(question.id, 'label', e.target.value)}
                        className="form-input w-full px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Which class types would you like to see more of?"
                      />
                    </div>
                  </div>

                  {(question.type === 'single' || question.type === 'multi') && (
                    <div className="space-y-2 mb-3">
                      {question.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center gap-2">
                          <span className="text-xs text-muted w-6">{optIndex + 1}.</span>
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(question.id, optIndex, e.target.value)}
                            className="form-input flex-1 px-3 py-1.5 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder={`Option ${optIndex + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeOption(question.id, optIndex)}
                            className="text-red-400 hover:text-red-600 text-sm"
                            title="Remove option"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addOption(question.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        + Add option
                      </button>
                    </div>
                  )}

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={question.required}
                      onChange={(e) => updateQuestion(question.id, 'required', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm text-secondary pl-2">Required</span>
                  </label>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={addQuestion}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20 transition-colors"
              >
                + Add Question
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/surveys')}
              className="px-6 py-2.5 text-sm font-semibold text-secondary bg-secondary border border-secondary rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 hover:border-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Survey'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SurveyEditor;